import { fromZonedTime, toZonedTime } from "date-fns-tz";
import {
  startOfDay,
  startOfWeek,
  endOfDay,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subDays,
  addDays,
  addWeeks,
  subMonths,
  addMonths,
} from "date-fns";
import { getStartOfDayInTimezone } from "@shared/utils/dates/parsing";

export const getToday = (timezone: string): Date =>
  getStartOfDayInTimezone(timezone, new Date());

export const getYesterday = (timezone: string): Date => {
  const zoned = toZonedTime(new Date(), timezone);
  const yesterday = subDays(zoned, 1);
  const midnight = startOfDay(yesterday);
  return fromZonedTime(midnight, timezone);
};

export const getLastWeekend = (
  timezone: string
): { startDate: Date; endDate: Date } => {
  const now = toZonedTime(new Date(), timezone);
  // Moment’s `.startOf("week").subtract(2, "days")` → Friday of the previous week
  const startOfCurrentWeek = startOfWeek(now, { weekStartsOn: 0 }); // Sunday start
  const startDateZoned = subDays(startOfCurrentWeek, 2);
  const endDateZoned = endOfDay(startOfCurrentWeek);
  return {
    startDate: fromZonedTime(startDateZoned, timezone),
    endDate: fromZonedTime(endDateZoned, timezone),
  };
};

export const getLastWeek = (
  timezone: string
): { startDate: Date; endDate: Date } => {
  const now = toZonedTime(new Date(), timezone);
  const startOfCurrentWeek = startOfWeek(now, { weekStartsOn: 0 });
  const startDateZoned = subDays(startOfCurrentWeek, 7);
  const endDateZoned = endOfDay(subDays(startOfCurrentWeek, 1));
  return {
    startDate: fromZonedTime(startDateZoned, timezone),
    endDate: fromZonedTime(endDateZoned, timezone),
  };
};

export const getThisMonth = (
  timezone: string
): { startDate: Date; endDate: Date } => {
  const now = toZonedTime(new Date(), timezone);
  const startDateZoned = startOfMonth(now);
  const endDateZoned = endOfMonth(now);
  return {
    startDate: fromZonedTime(startDateZoned, timezone),
    endDate: fromZonedTime(endDateZoned, timezone),
  };
};

export const getLastMonth = (
  timezone: string
): { startDate: Date; endDate: Date } => {
  const now = toZonedTime(new Date(), timezone);
  const lastMonthDate = subMonths(now, 1);
  const startDateZoned = startOfMonth(lastMonthDate);
  const endDateZoned = endOfMonth(lastMonthDate);
  return {
    startDate: fromZonedTime(startDateZoned, timezone),
    endDate: fromZonedTime(endDateZoned, timezone),
  };
};

export const getTodayStartAndEndDate = (
  timezone: string
): { startDate: Date; endDate: Date } => {
  const now = toZonedTime(new Date(), timezone);
  return {
    startDate: fromZonedTime(startOfDay(now), timezone),
    endDate: fromZonedTime(endOfDay(now), timezone),
  };
};

export const getTomorrowStartAndEndDate = (
  timezone: string
): { startDate: Date; endDate: Date } => {
  const now = toZonedTime(new Date(), timezone);
  const tomorrow = addDays(now, 1);
  return {
    startDate: fromZonedTime(startOfDay(tomorrow), timezone),
    endDate: fromZonedTime(endOfDay(tomorrow), timezone),
  };
};

export const getThisWeekendStartAndEndDate = (
  timezone: string
): { startDate: Date; endDate: Date } => {
  const now = toZonedTime(new Date(), timezone);
  const nextWeek = addWeeks(now, 1);
  const startOfNextWeek = startOfWeek(nextWeek, { weekStartsOn: 1 });
  const startDateZoned = subDays(startOfNextWeek, 2);
  const endDateZoned = endOfDay(subDays(startOfNextWeek, 1));
  return {
    startDate: fromZonedTime(startDateZoned, timezone),
    endDate: fromZonedTime(endDateZoned, timezone),
  };
};

export const getNextWeekStartAndEndDate = (
  timezone: string
): { startDate: Date; endDate: Date } => {
  const now = toZonedTime(new Date(), timezone);
  const nextWeek = addWeeks(now, 1);
  const startDateZoned = startOfWeek(nextWeek, { weekStartsOn: 1 });
  const endDateZoned = endOfWeek(nextWeek, { weekStartsOn: 1 });
  return {
    startDate: fromZonedTime(startDateZoned, timezone),
    endDate: fromZonedTime(endDateZoned, timezone),
  };
};

export const getThisMonthStartAndEndDate = (
  timezone: string
): { startDate: Date; endDate: Date } => {
  const now = toZonedTime(new Date(), timezone);
  return {
    startDate: fromZonedTime(startOfMonth(now), timezone),
    endDate: fromZonedTime(endOfMonth(now), timezone),
  };
};

export const getNextMonthStartAndEndDate = (
  timezone: string
): { startDate: Date; endDate: Date } => {
  const now = toZonedTime(new Date(), timezone);
  const nextMonth = addMonths(now, 1);
  return {
    startDate: fromZonedTime(startOfMonth(nextMonth), timezone),
    endDate: fromZonedTime(endOfMonth(nextMonth), timezone),
  };
};
